/**
 * REUSABLE REPAYMENT TABLE COMPONENT
 * Displays repayment data in simple row format
 * Shows: Sr No, Borrower, Facility Type, Phase, Amount, KIBOR, Spread%, Rate%, Rep Date, Rep Amount
 */

import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { RepaymentReportRow } from '../models/report.model';
import { ReportPivotService } from '../services/report-pivot.service';

@Component({
  selector: 'app-repayment-table',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  template: `
    <div class="bg-[#FAFAFA] rounded-[20px] p-4 sm:p-6">
      <!-- Header with title and actions -->
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-lg sm:text-xl font-semibold text-[#444A6D]">{{ displayTitle || title }}</h2>

        <div class="flex gap-2">
          <button
            (click)="onExportCSV()"
            class="text-sm px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
          >
            📥 {{ 'REPORTS.TABLE.EXPORT_EXCEL' | translate }}
          </button>
        </div>
      </div>

      <!-- Table -->
      <div class="relative overflow-x-auto w-full bg-white rounded-lg shadow">
        <table class="w-full border-collapse">
          <!-- HEADER ROW -->
          <thead>
            <tr class="bg-indigo-600 text-white sticky top-0">
              <th class="px-4 py-3 text-start font-semibold">{{ 'REPORTS.TABLE.SR_NO' | translate }}</th>
              <th class="px-4 py-3 text-start font-semibold">{{ 'REPORTS.TABLE.BORROWER_NAME' | translate }}</th>
              <th class="px-4 py-3 text-start font-semibold">{{ 'REPORTS.TABLE.FACILITY_TYPE' | translate }}</th>
              <th class="px-4 py-3 text-start font-semibold">{{ 'REPORTS.TABLE.PHASE' | translate }}</th>
              <th class="px-4 py-3 text-end font-semibold">{{ 'REPORTS.TABLE.FACILITY_AMOUNT' | translate }}</th>
              <th class="px-4 py-3 text-end font-semibold">{{ 'REPORTS.TABLE.SPREAD_PCT' | translate }}</th>
              <th class="px-4 py-3 text-end font-semibold">{{ 'REPORTS.TABLE.KIBOR_RATE_PCT' | translate }}</th>
              <th class="px-4 py-3 text-end font-semibold">{{ 'REPORTS.TABLE.TOTAL_RATE_PCT' | translate }}</th>
              <th class="px-4 py-3 text-start font-semibold">{{ 'REPORTS.TABLE.MARKUP_PAID_DATE' | translate }}</th>

              <th class="px-4 py-3 text-end font-semibold">{{ 'REPORTS.TABLE.MARKUP_PAID' | translate }}</th>
              <th class="px-4 py-3 text-start font-semibold">{{ 'REPORTS.TABLE.REPAYMENT_DATE' | translate }}</th>

              <th class="px-4 py-3 text-end font-semibold">{{ 'REPORTS.TABLE.PRINCIPAL_REPAYMENT_AMOUNT' | translate }}</th>
            </tr>
          </thead>

          <!-- DATA ROWS -->
          <tbody>
            @for (row of rows; track row.srNo; let isEven = $even) {
              <tr [class.bg-gray-50]="isEven" class="border-b border-gray-200 hover:bg-indigo-50">
                <td class="px-4 py-3 font-semibold text-gray-700">
                  {{ row.srNo }}
                </td>

                <td class="px-4 py-3 text-gray-700">
                  {{ row.borrowerName }}
                </td>

                <td class="px-4 py-3 text-gray-700">
                  {{ row.fcltyTyp | translate }}
                </td>

                <td class="px-4 py-3 text-gray-700">
                  {{ row.phsKey | translate }}
                </td>

                <td class="px-4 py-3 text-end text-gray-700 font-medium">
                  {{ formatCurrency(row.fcltyAmount) }}
                </td>

                <td class="px-4 py-3 text-end text-gray-700">
                  {{ formatPercentage(row.spread) }}
                </td>

                <td class="px-4 py-3 text-end text-gray-700">
                  @if (row.kiborRt !== null && row.kiborRt !== undefined) {
                    {{ formatPercentage(row.kiborRt) }}
                  } @else {
                    -
                  }
                </td>

                <td class="px-4 py-3 text-end text-gray-700">
                  @if (row.totalRt !== null && row.totalRt !== undefined) {
                    {{ formatPercentage(row.totalRt) }}
                  } @else {
                    -
                  }
                </td>
                <td class="px-4 py-3 text-gray-700">
                  {{ row.markupPaidDate || '-' }}
                </td>
                <td class="px-4 py-3 text-end text-gray-700 font-medium">
                  {{ formatCurrency(row.markupPaidAmount) }}
                </td>

                <td class="px-4 py-3 text-gray-700">
                  {{ row.repaymentDate || '-' }}
                </td>

                <td class="px-4 py-3 text-end text-gray-700 font-medium">
                  {{ formatCurrency(row.repaymentAmount) }}
                </td>
              </tr>
            }
          </tbody>

          <!-- FOOTER: Grand Total Row -->
          <tfoot>
            <tr class="bg-indigo-100 font-bold border-t-2 border-indigo-600">
              <td class="px-4 py-3">-</td>
              <td class="px-4 py-3">{{ 'REPORTS.TABLE.GRAND_TOTAL' | translate }}</td>
              <td class="px-4 py-3">-</td>
              <td class="px-4 py-3">-</td>

              <td class="px-4 py-3 text-end text-indigo-900 bg-indigo-100">
                {{ formatCurrency(calculateFacilityAmountTotal()) }}
              </td>

              <td class="px-4 py-3">-</td>
              <td class="px-4 py-3">-</td>
              <td class="px-4 py-3">-</td>
              <td class="px-4 py-3">-</td>

              <td class="px-4 py-3 text-end text-indigo-900 bg-indigo-100">
                {{ formatCurrency(calculateMarkupPaidTotal()) }}
              </td>

             <!-- Repayment Date -->
<td class="px-4 py-3">-</td>

<!-- Repayment Amount -->
<td class="px-4 py-3 text-end text-indigo-900 bg-indigo-100">
  {{ formatCurrency(calculateGrandTotal()) }}
</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Footer Info -->
      <div class="p-4 bg-gray-50 border-t border-gray-200 text-sm text-gray-600">
        {{ 'REPORTS.TABLE.TOTAL_RECORDS' | translate }} <span class="font-semibold">{{ rows.length }}</span>
      </div>
    </div>
  `,
  styles: [
    `
      .overflow-x-auto::-webkit-scrollbar {
        height: 8px;
      }

      .overflow-x-auto::-webkit-scrollbar-track {
        background: #f1f5f9;
      }

      .overflow-x-auto::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 4px;
      }

      .overflow-x-auto::-webkit-scrollbar-thumb:hover {
        background: #94a3b8;
      }

      table {
        min-width: 100%;
      }

      th,
      td {
        white-space: nowrap;
      }
    `,
  ],
})
export class RepaymentTableComponent {
  @Input() title: string = 'Repayment Report';
  /** Optional translated title shown instead of `title` (`title` stays the raw value used in logic). */
  @Input() displayTitle = '';
  @Input() rows: RepaymentReportRow[] = [];
  @Input() startDate: Date | string | null = null;
  @Input() endDate: Date | string | null = null;

  @Output() exportCSV = new EventEmitter<void>();

  constructor(private pivotService: ReportPivotService) { }

  /**
   * Format number as currency with 2 decimal places for precision
   */
  formatCurrency(value: number | null | undefined): string {
    if (value == null) {
      return '0';
    }
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  /**
   * Format number as percentage with 2 decimal places for consistency
   */
  formatPercentage(value: number | null | undefined): string {
    if (value == null) return '-';

    return value.toFixed(2) + '%';
  }

  /**
   * Calculate grand total of all repayments
   */
  calculateGrandTotal(): number {
    return this.rows.reduce((sum, row) => sum + (row.repaymentAmount || 0), 0);
  }

  /**
   * Calculate total markup paid
   */
  calculateMarkupPaidTotal(): number {
    return this.rows.reduce((sum, row) => sum + (row.markupPaidAmount || 0), 0);
  }

  /**
   * Calculate total facility amount
   */
  calculateFacilityAmountTotal(): number {
    return this.rows.reduce((sum, row) => sum + (row.fcltyAmount || 0), 0);
  }

  /**
   * Export to Excel
   */
  onExportCSV(): void {
    const filename = `repayment-report-${new Date().toISOString().split('T')[0]}.xlsx`;

    this.pivotService.exportRepaymentReportToExcel(
      this.rows,
      filename,
      this.startDate,
      this.endDate,
    );

    this.exportCSV.emit();
  }
}
